/**
 * Top chrome matching HealthcarePortal page header: brand, appearance (accent/theme), read-only user block (no links, no sign-out).
 */
import React, { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useActiveRole } from '../../context/ActiveRoleContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { AppearancePopover } from '../Appearance/AppearancePopover';
import { capitalizeWords } from '../../utils/stringFunctions.utils';
import { useHealthesusLogomarkSrc } from '../../hooks/useHealthesusLogomarkSrc';
import './PortalPageHeader.css';

/** Synced to `document.documentElement` `--nav-height` when this header is mounted; use for layout when header is hidden (e.g. `/examform`). */
export const PORTAL_PAGE_HEADER_HEIGHT_PX = 64;

export const PortalPageHeader: React.FC = () => {
  const healthesusLogomarkSrc = useHealthesusLogomarkSrc();
  const { isAuthenticated, user, userProfile: contextUserProfile, isLoggingIn, isLoading } = useAuth();
  const { activeRole, availableRoles } = useActiveRole();
  const { data: fetchedUserProfile } = useUserProfile(contextUserProfile ? null : (user?.id ?? null));
  const userProfile = contextUserProfile ?? fetchedUserProfile;

  const hasLoginError = sessionStorage.getItem('login_error') !== null;
  const isFullySignedIn = isAuthenticated && !!user && !hasLoginError;
  const hasProfileDisplayName = !!(userProfile?.firstName || userProfile?.lastName || userProfile?.userName);
  const shouldShowUser = isFullySignedIn && !isLoggingIn && !isLoading && hasProfileDisplayName;

  useEffect(() => {
    document.documentElement.style.setProperty('--nav-height', `${PORTAL_PAGE_HEADER_HEIGHT_PX}px`);
  }, []);

  const getRoleName = (role: unknown): string => {
    if (typeof role === 'string') return role;
    const r = role as { roleName?: string; name?: string };
    return (r?.roleName ?? r?.name ?? '').toLowerCase();
  };

  const hasProviderRole = (): boolean => {
    const profileRoles = userProfile?.roles || [];
    const activeRoleString = activeRole != null ? String(activeRole) : null;
    return (
      (activeRoleString && activeRoleString.toLowerCase() === 'provider') ||
      availableRoles.some((role) => typeof role === 'string' && role.toLowerCase() === 'provider') ||
      Boolean(user?.roles?.some((role) => typeof role === 'string' && role.toLowerCase() === 'provider')) ||
      (Array.isArray(profileRoles) &&
        profileRoles.some((role: unknown) => getRoleName(role) === 'provider'))
    );
  };

  const getDisplayName = (): string => {
    const firstName = userProfile?.firstName;
    const lastName = userProfile?.lastName;
    const isProvider = hasProviderRole();

    if (firstName && lastName) {
      const fullName = `${capitalizeWords(firstName)} ${capitalizeWords(lastName)}`.trim();
      return isProvider ? `Dr. ${fullName}` : fullName;
    }
    if (firstName) {
      const p = capitalizeWords(firstName);
      return isProvider ? `Dr. ${p}` : p;
    }
    if (lastName) {
      const p = capitalizeWords(lastName);
      return isProvider ? `Dr. ${p}` : p;
    }
    if (userProfile?.userName) {
      return capitalizeWords(userProfile.userName);
    }
    return '';
  };

  const getRoleLabel = (): string => {
    const role = activeRole != null ? String(activeRole).trim() : '';
    if (!role) return 'User';
    return capitalizeWords(role);
  };

  const getUserInitials = (): string => {
    const firstName = userProfile?.firstName;
    const lastName = userProfile?.lastName;

    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    }
    if (firstName) return firstName.substring(0, 2).toUpperCase();
    if (lastName) return lastName.substring(0, 2).toUpperCase();
    if (userProfile?.userName) return userProfile.userName.substring(0, 2).toUpperCase();
    return 'U';
  };

  const navClassName =
    'portal-page-header-chrome w-full min-w-0 bg-white dark:bg-[var(--background-primary)] sticky top-0 z-portal-chrome shadow-sm box-border';

  return (
    <nav className={navClassName} role="navigation" aria-label="Application header">
      <div className="nav-header-inner relative mx-auto w-full px-4 md:px-6">
        <div className="nav-header-row flex justify-between items-center min-h-[var(--nav-height)]">
          <div className="nav-header-left flex items-center min-w-0 flex-1">
            <div
              className="nav-header-brand flex items-center flex-shrink-0 text-inherit"
              aria-label="Healthesus Exam Pro Forms"
            >
              <div className="nav-header-logomark-wrap">
                <img
                  src={healthesusLogomarkSrc}
                  alt="Healthesus"
                  className="nav-header-logo"
                  width={32}
                  height={32}
                />
              </div>
              <span className="nav-header-product-name whitespace-nowrap">Exam Pro Forms</span>
            </div>
          </div>

          <div className="nav-header-right flex items-center flex-shrink-0">
            <AppearancePopover />
            {shouldShowUser ? (
              <div
                className="nav-header-user-block user-menu-container relative"
                role="group"
                aria-label="Your profile (read-only)"
              >
                {/* Same shell as HCP Navigation user menu; non-interactive in Exam Pro Forms (no dropdown). */}
                <div
                  className="nav-header-user-block-btn portal-chrome-user-readonly"
                  tabIndex={-1}
                >
                  <div className="nav-header-user-block-btn-inner">
                    <div className="nav-header-user-avatar" aria-hidden>
                      {getUserInitials()}
                    </div>
                    <div className="hidden lg:flex lg:items-center lg:min-w-0 lg:flex-1 lg:gap-2">
                      <div className="flex flex-col min-w-0">
                        <span className="nav-header-user-name user-menu-username truncate">
                          {getDisplayName()}
                        </span>
                        <span className="nav-header-status-label flex items-center mt-0.5">
                          <span className="w-2 h-2 rounded-full mr-1.5 bg-green-500 shrink-0" aria-hidden />
                          {getRoleLabel()} • Online
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  );
};